import type { AuthorStats } from '@/data/types';
import { SPILLER_NAVN_DEFAULT } from '@/data/types';
import { STAT_LABEL } from '@/data/courseCatalog';
import type { SkrivelabAnalyse } from './skrivelab';

export type VurderingsStemme = 'kristine' | 'haldis' | 'bjorn' | 'egen-forfatter';

export type VurderingsResultat = {
  stemme: VurderingsStemme;
  npcNavn: string;
  npcRolle: string;
  hilsen: string;
  tilbakemelding: string;
  avslutning: string;
  fokusStat: keyof AuthorStats;
};

type StemmeProfil = {
  navn: string;
  rolle: string;
  hilsener: ReadonlyArray<string>;
  avslutninger: ReadonlyArray<string>;
  faserHoy: Record<keyof AuthorStats, ReadonlyArray<string>>;
  faserLav: Record<keyof AuthorStats, ReadonlyArray<string>>;
};

const KRISTINE: StemmeProfil = {
  navn: 'Kristine',
  rolle: 'mentor og rektor ved Forfatterskolen',
  hilsener: [
    'Hei {navn} — ',
    'Kjære {navn}, ',
    '{navn}, du. ',
    'Hei på deg, ',
  ],
  avslutninger: [
    'Fortsett å skrive — det er noe der.',
    'Du er på vei. Bli ved skrivebordet.',
    'Jeg gleder meg til neste utkast.',
    'Pleie det du har — det er verdt det.',
  ],
  faserHoy: {
    dialog: [
      'Du har et øye for dialog. Det er sjeldent.',
      'Replikkene dine pust — du hører dem allerede.',
      'Dialogen er der det skjer, og du vet det.',
    ],
    plot: [
      'Du har driv i fortellingen — det merkes.',
      'Det er en retning her som ikke er tilfeldig.',
      'Du tør å la handling gjøre arbeidet.',
    ],
    karakter: [
      'Karakterene står frem — du gir dem rom.',
      'Du har psykologisk presisjon i hvordan figurene oppfører seg.',
      'Personene dine er ikke bare navn — de er mennesker.',
    ],
    spraak: [
      'Språket bærer. Du har rytme.',
      'Setningene er stødige uten å virke anstrengt.',
      'Det er en sikker hånd i prosaen din.',
    ],
    voice: [
      'Det er en stemme her som er din alene.',
      'Tonen er gjenkjennelig allerede etter to setninger.',
      'Du våger å være deg selv på papiret.',
    ],
    autentisitet: [
      'Du forankrer scenen i sansene — det merkes.',
      'Det er en kropp i denne prosaen, og det gir den vekt.',
      'Du tror på det du skriver, og det smitter.',
    ],
    disiplin: [
      'Du har redigert godt. Det er rent.',
      'Du veksler rytme uten å miste taket.',
      'Det er en disiplin her som ikke er stiv.',
    ],
  },
  faserLav: {
    dialog: [
      'Vurder å pusse litt mer på dialogen — det er rom.',
      'La personene snakke mer. Du holder dem litt tilbake.',
      'En velplassert replikk kan løfte scenen.',
    ],
    plot: [
      'Plot kan du jobbe mer med — la noe skje tidlig.',
      'Det er litt indre stillstand. Sett en spire i jorda.',
      'Tenk på hva karakteren faktisk gjør, ikke bare tenker.',
    ],
    karakter: [
      'Karakterene kan komme tydeligere frem.',
      'Gi figurene en konkret detalj som skiller dem ut.',
      'Vi savner et indre liv hos personene.',
    ],
    spraak: [
      'Språket kan strammes — for mange ord gjentas.',
      'Variér setningsstrukturen mer — det er litt monotont.',
      'Et bredere ordforråd vil gi prosaen mer farge.',
    ],
    voice: [
      'Stemmen din kan komme tydeligere frem — våg.',
      'Teksten er litt anonym her — finn det bare du kan si.',
      'Bytt ut det forsiktige med noe som er ditt.',
    ],
    autentisitet: [
      'Mer sansing vil forankre scenen.',
      'Vær konkret — bord, kaffe, regn. Ting som veier.',
      'Teksten svever litt — la den lande i en kropp.',
    ],
    disiplin: [
      'Vask gjennom — det er småfeil og uvariert rytme.',
      'Les høyt før neste utkast — du vil høre hva som halter.',
      'En grundig korrektur vil løfte teksten et hakk.',
    ],
  },
};

const HALDIS: StemmeProfil = {
  navn: 'Haldis',
  rolle: 'streng sjefredaktør',
  hilsener: [
    '{navn}. ',
    'OK, {navn}. ',
    'Hør her, ',
    'La meg være rett frem: ',
  ],
  avslutninger: [
    'Du er nesten der. Nesten.',
    'Jobb mer. Send på nytt.',
    'Det blir bedre. Hold på.',
    'Jeg har sett verre. Fortsett.',
  ],
  faserHoy: {
    dialog: [
      'Bra håndverk på dialog. Vet hva du gjør der.',
      'Replikkene fungerer. Greit.',
      'Du har øre. Bruk det videre.',
    ],
    plot: [
      'Plot sitter. Det er momentum.',
      'Du driver historien framover. Bra.',
      'Det er en retning her. Hold den.',
    ],
    karakter: [
      'Karakterene står. Solid.',
      'Du har grep om personene. Det merkes.',
      'Figurene puster. Greit jobba.',
    ],
    spraak: [
      'Språket er stramt. Bra.',
      'Setningene gjør jobben sin.',
      'Du har orden i prosaen.',
    ],
    voice: [
      'Stemmen er der. Hold på den.',
      'Du har en tone. Ikke mist den.',
      'Det høres ut som deg. Bra.',
    ],
    autentisitet: [
      'Det er konkret. Du forankrer.',
      'Sansene er på plass. Greit.',
      'Det er kjøtt og bein her.',
    ],
    disiplin: [
      'Du har redigert. Det vises.',
      'Ren tekst. Bra håndverk.',
      'Strammet og rensket. Greit.',
    ],
  },
  faserLav: {
    dialog: [
      'Men dialogen trenger arbeid. Mye arbeid.',
      'Få dem til å snakke. Nå er det stumt.',
      'Replikkene er flate. Skjerp dem.',
    ],
    plot: [
      'Plot mangler. Det skjer ikke noe.',
      'Bevegelse, takk. Det står stille.',
      'Du må få ting til å skje.',
    ],
    karakter: [
      'Karakterene er blasse. Skarp dem.',
      'Hvem er disse menneskene? Jeg vet ikke.',
      'Gi meg et navn jeg husker.',
    ],
    spraak: [
      'Språket halter. Vask det.',
      'For mange gjentakelser. Plukk dem ut.',
      'Setningene blir slappe. Stram.',
    ],
    voice: [
      'Stemmen er anonym. Finn din egen.',
      'Du gjemmer deg i prosaen. Kom frem.',
      'Det høres ut som hvem som helst.',
    ],
    autentisitet: [
      'Det svever. Få det ned på jorda.',
      'Konkrete ting, takk. Jeg vil se det.',
      'Sanser, ikke abstraksjoner.',
    ],
    disiplin: [
      'Vask. Det er feil. Det er rusk.',
      'Korrektur før neste utkast. Hver gang.',
      'Du har ikke redigert. Det vises.',
    ],
  },
};

const BJORN: StemmeProfil = {
  navn: 'Bjørn',
  rolle: 'utbrent forfatter-kollega',
  hilsener: [
    '{navn}, du. ',
    'Hør her, kompis. ',
    'Greit, jeg leste det. ',
    'OK. ',
  ],
  avslutninger: [
    'Jeg har sett verre. Mye verre.',
    'Pass på deg selv. Pass på pennen.',
    'Du minner meg om meg selv før jeg brant ut.',
    'Skriv videre. Det er det vi har.',
  ],
  faserHoy: {
    dialog: [
      'Dialogen er bra. Du hører folk.',
      'Replikkene sitter. Sjelden vare.',
      'Du har øre for det. Ikke kast det bort.',
    ],
    plot: [
      'Det skjer noe. Det er mye allerede.',
      'Du driver det framover. Slik skal det være.',
      'Det er bevegelse. Det er liv.',
    ],
    karakter: [
      'Karakterene står. De er ekte for meg.',
      'Du gir dem rom. Det er sjeldent.',
      'Folkene dine puster. Hold på det.',
    ],
    spraak: [
      'Språket er der. Du har rytme.',
      'Setningene rull. Jeg liker det.',
      'Du har en hånd som vet.',
    ],
    voice: [
      'Du har en stemme. Det er det viktigste.',
      'Det høres ut som deg. Slik må det være.',
      'Du tør. Det er halve jobben.',
    ],
    autentisitet: [
      'Du forankrer. Det er konkret.',
      'Det er en kropp her. Bra.',
      'Du sanser. Slik må man.',
    ],
    disiplin: [
      'Du har redigert. Det merker man.',
      'Ren prosa. Du har gjort jobben.',
      'Strammet. Bra.',
    ],
  },
  faserLav: {
    dialog: [
      'Men pass på dialogen. Den er litt stum.',
      'Få dem til å snakke. Det er der det skjer.',
      'Replikkene kan løfte deg ut av referatet.',
    ],
    plot: [
      'Det står litt stille. Pass på det.',
      'Du må få noe til å skje. Tidlig.',
      'Indre stillstand er en felle. Jeg vet.',
    ],
    karakter: [
      'Karakterene er litt vage. Pass på det.',
      'Gi dem en detalj. Bare én.',
      'Jeg ser dem ikke ennå.',
    ],
    spraak: [
      'Språket gjentar seg. Pass på det.',
      'Variér mer. Du vet det.',
      'Det er litt monotont. Bryt det.',
    ],
    voice: [
      'Stemmen kan tydeligere frem.',
      'Du gjemmer deg litt. Kom ut.',
      'Vær mer deg. Det er det vi vil ha.',
    ],
    autentisitet: [
      'Det svever litt. Få det ned.',
      'Mer sanser. Hud, lukt, lyd.',
      'Konkret, kompis. Konkret.',
    ],
    disiplin: [
      'Korrekturen er ikke ferdig. Vask en runde til.',
      'Les høyt. Du vil høre det.',
      'Småfeil bygger seg opp. Plukk dem.',
    ],
  },
};

const PROFILER: Record<VurderingsStemme, StemmeProfil> = {
  kristine: KRISTINE,
  haldis: HALDIS,
  bjorn: BJORN,
  'egen-forfatter': KRISTINE,
};

export function genererNpcVurdering(
  analyse: SkrivelabAnalyse,
  stemme: VurderingsStemme,
  rng: () => number = Math.random,
  spillerNavn: string = SPILLER_NAVN_DEFAULT,
): VurderingsResultat {
  const profil = PROFILER[stemme];
  const fokusStat = analyse.fokusStat;
  const svakStat = analyse.svakeste;

  const navn = spillerNavn.trim().length > 0 ? spillerNavn.trim() : SPILLER_NAVN_DEFAULT;
  const hilsen = settInnNavn(velg(profil.hilsener, rng), navn);
  const avslutning = settInnNavn(velg(profil.avslutninger, rng), navn);

  const sterkLabel = STAT_LABEL[fokusStat];
  const svakLabel = STAT_LABEL[svakStat];
  const sterkFrase = settInnNavn(velg(profil.faserHoy[fokusStat], rng), navn);
  const svakFrase = settInnNavn(velg(profil.faserLav[svakStat], rng), navn);

  const tilbakemelding = `Sterkest: ${sterkLabel}. ${sterkFrase} Mulighet: ${svakLabel}. ${svakFrase}`;

  return {
    stemme,
    npcNavn: profil.navn,
    npcRolle: profil.rolle,
    hilsen,
    tilbakemelding,
    avslutning,
    fokusStat,
  };
}

function settInnNavn(tekst: string, navn: string): string {
  return tekst.replace(/\{navn\}/g, navn);
}

function velg<T>(liste: ReadonlyArray<T>, rng: () => number): T {
  const i = Math.floor(rng() * liste.length);
  return liste[Math.min(liste.length - 1, Math.max(0, i))];
}

export function npcNavn(stemme: VurderingsStemme): string {
  return PROFILER[stemme].navn;
}

export function npcRolle(stemme: VurderingsStemme): string {
  return PROFILER[stemme].rolle;
}
